import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import FileUpload from '../../components/common/FileUpload';
import AdvancedSearch, { SearchField, SearchCondition, conditionsToQueryParams, queryParamsToConditions } from '../../components/common/AdvancedSearch';
import Toast from '../../components/common/Toast';
import { exportToCSV, importCSVFile, parseCSV } from '../../utils/csv';
import { Request, RequestCSV } from '../../types/request';

interface Company {
  id: string;
  name: string;
}

interface Area {
  id: string;
  name: string;
}

interface User {
  id: string;
  name: string;
}

interface PaginatedResponse {
  items: Request[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const initialFormData = {
  title: '',
  companyId: '',
  areaId: '',
  content: '',
  status: 'pending',
  priority: 'normal',
  assignedTo: '',
  dueDate: '',
  emailSubject: '',
  emailBody: '',
  emailFrom: '',
  emailDate: '',
};

export default function RequestList() {
  const { user } = useAuth();
  const { canCreate } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const quickSearch = searchParams.get('search') || '';
  const [requests, setRequests] = useState<Request[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const isFirstLoadRef = useRef(true);
  const [searchConditions, setSearchConditions] = useState<SearchCondition[]>(() => {
    const saved = searchParams.get('conditions');
    return saved ? queryParamsToConditions(saved) : [];
  });
  const [showCreateModal, setShowCreateModal] = useState(false);

  // インポート権限: 管理者とプロモーターのみ
  const canImport = user?.roleCode === 'admin' || user?.roleCode === 'promoter';
  
  // 作成権限
  const canCreateRequest = canCreate('requests');

  // クイックアクション: ?action=new でモーダルを自動で開く（create権限チェック）
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      if (canCreateRequest) setShowCreateModal(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; url: string; type: string }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'createdAt',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });

  // 詳細検索フィールド定義
  const searchFields: SearchField[] = useMemo(() => [
    { key: 'requestNumber', label: '要請番号', type: 'text', placeholder: 'REQ-2024-001' },
    { key: 'title', label: 'タイトル', type: 'text', placeholder: 'タイトルで検索' },
    { key: 'companyId', label: '企業', type: 'select', options: companies.map(c => ({ value: c.id, label: c.name })) },
    { key: 'status', label: 'ステータス', type: 'select', options: [
      { value: 'pending', label: '対応待ち' },
      { value: 'in_progress', label: '対応中' },
      { value: 'completed', label: '完了' },
      { value: 'cancelled', label: 'キャンセル' },
    ]},
    { key: 'priority', label: '優先度', type: 'select', options: [
      { value: 'low', label: '低' },
      { value: 'normal', label: '通常' },
      { value: 'high', label: '高' },
      { value: 'urgent', label: '緊急' },
    ]},
    { key: 'assignedTo', label: '担当者', type: 'select', options: users.map(u => ({ value: u.id, label: u.name })) },
    { key: 'createdAt', label: '登録日', type: 'date' },
    { key: 'dueDate', label: '期限日', type: 'date' },
  ], [companies, users]);

  useEffect(() => {
    fetchRequests();
  }, [searchConditions, currentPage, pageSize, sortConfig, quickSearch]);

  useEffect(() => {
    fetchCompanies();
    fetchAreas();
    fetchUsers();
  }, []);

  // 検索条件をURLに保存（他パラメータを維持）
  useEffect(() => {
    setSearchParams(prev => {
      if (searchConditions.length > 0) {
        prev.set('conditions', conditionsToQueryParams(searchConditions));
      } else {
        prev.delete('conditions');
      }
      return prev;
    }, { replace: true });
  }, [searchConditions, setSearchParams]);

  const fetchCompanies = async () => {
    try {
      const data = await api.get<{ items: Company[] }>('/api/companies?status=active&limit=1000');
      setCompanies(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setCompanies([]);
    }
  };

  const fetchAreas = async () => {
    try {
      const data = await api.get<{ items: Area[] }>('/api/masters/areas?limit=1000');
      setAreas(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setAreas([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await api.get<{ items: User[] }>('/api/users?status=active&limit=1000');
      setUsers(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setUsers([]);
    }
  };

  const fetchRequests = async () => {
    if (isFirstLoadRef.current) setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (quickSearch) {
        params.append('search', quickSearch);
      }
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      
      params.append('page', currentPage.toString());
      params.append('pageSize', pageSize.toString());
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);
      const data = await api.get<PaginatedResponse>(`/api/requests?${params}`);
      setRequests(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      setRequests([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
      isFirstLoadRef.current = false;
    }
  };

  const handleSearch = (conditions: SearchCondition[]) => {
    setSearchConditions(conditions);
    setSearchParams(prev => {
      prev.delete('page');
      return prev;
    });
  };

  const defaultSort = { key: 'createdAt', direction: 'desc' as const };
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      let next: { key: string; direction: 'asc' | 'desc' };
      if (prev.key !== key) next = { key, direction: 'asc' };
      else if (prev.direction === 'asc') next = { key, direction: 'desc' };
      else next = defaultSort;
      setSearchParams(p => { p.set('sortBy', next.key); p.set('sortOrder', next.direction); p.set('page', '1'); return p; }, { replace: true });
      return next;
    });
  };

  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState<RequestCSV[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  // ステータス・優先度の日本語⇔英語変換マップ
  const statusLabels: Record<string, string> = { pending: '対応待ち', in_progress: '対応中', completed: '完了', cancelled: 'キャンセル' };
  const statusCodes: Record<string, string> = { '対応待ち': 'pending', '対応中': 'in_progress', '完了': 'completed', 'キャンセル': 'cancelled' };
  const priorityLabels: Record<string, string> = { low: '低', normal: '通常', high: '高', urgent: '緊急' };
  const priorityCodes: Record<string, string> = { '低': 'low', '通常': 'normal', '高': 'high', '緊急': 'urgent' };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // ページネーションで全件取得（検索条件は適用）
      let allRequests: Request[] = [];
      let page = 1;
      const exportPageSize = 500;
      while (true) {
        const params = new URLSearchParams();
        if (searchConditions.length > 0) {
          params.append('advancedSearch', JSON.stringify(searchConditions));
        }
        params.append('page', String(page));
        params.append('pageSize', String(exportPageSize));
        params.append('sortBy', sortConfig.key);
        params.append('sortOrder', sortConfig.direction);
        const data = await api.get<PaginatedResponse>(`/api/requests?${params}`);
        const items = Array.isArray(data?.items) ? data.items : [];
        allRequests = [...allRequests, ...items];
        if (allRequests.length >= (data?.total ?? 0) || items.length === 0) break;
        page++;
      }

      // 全項目をエクスポート
      const csvColumns = [
        { key: 'requestNumber' as const, header: '要請番号' },
        { key: 'title' as const, header: 'タイトル' },
        { key: 'content' as const, header: '内容' },
        { key: 'companyName' as const, header: '企業名' },
        { key: 'areaName' as const, header: 'エリア名' },
        { key: 'status' as const, header: 'ステータス', format: (v: unknown) => statusLabels[v as string] || String(v) },
        { key: 'priority' as const, header: '優先度', format: (v: unknown) => priorityLabels[v as string] || String(v) },
        { key: 'assignedToName' as const, header: '担当者名' },
        { key: 'dueDate' as const, header: '期限日', format: (v: unknown) => v ? new Date(v as string).toLocaleDateString('ja-JP') : '' },
        { key: 'emailSubject' as const, header: 'メール件名' },
        { key: 'emailFrom' as const, header: 'メール送信者' },
        { key: 'emailBody' as const, header: 'メール本文' },
        { key: 'emailDate' as const, header: 'メール日時', format: (v: unknown) => v ? new Date(v as string).toLocaleString('ja-JP') : '' },
        { key: 'createdAt' as const, header: '登録日', format: (v: unknown) => new Date(v as string).toLocaleDateString('ja-JP') },
      ];
      exportToCSV(allRequests as unknown as Record<string, unknown>[], csvColumns, '要請一覧');
    } catch (error) {
      console.error('CSV export failed:', error);
      setToast({message: 'CSVエクスポートに失敗しました', type: 'error'});
    } finally {
      setExporting(false);
    }
  };

  const handleImportCSV = async () => {
    try {
      const csvContent = await importCSVFile();
      const columnMapping: Record<string, keyof RequestCSV> = {
        '要請番号': '要請番号',
        'タイトル': 'タイトル',
        '内容': '内容',
        '企業名': '企業名',
        'エリア名': 'エリア名',
        'ステータス': 'ステータス',
        '優先度': '優先度',
        '担当者名': '担当者名',
        '期限日': '期限日',
        'メール件名': 'メール件名',
        'メール送信者': 'メール送信者',
        'メール本文': 'メール本文',
        'メール日時': 'メール日時',
        '登録日': '登録日',
      };
      const parsed = parseCSV<RequestCSV>(csvContent, columnMapping);
      setImportPreview(parsed);
      setImportErrors([]);
      setShowImportModal(true);
    } catch (error) {
      console.error('CSV parse failed:', error);
      setToast({message: 'CSVファイルの読み込みに失敗しました', type: 'error'});
    }
  };

  const executeImport = async () => {
    if (importing) return;
    setImporting(true);
    const errors: string[] = [];
    let successCount = 0;

    try {
      // 要請番号を一括取得してMapにキャッシュ（N+1回避）
      const requestNumbers = importPreview
        .map(row => row.要請番号)
        .filter(Boolean);
      const existingMap = new Map<string, string>();
      if (requestNumbers.length > 0) {
        const batchSize = 500;
        for (let offset = 0; offset < requestNumbers.length; offset += batchSize) {
          const batch = requestNumbers.slice(offset, offset + batchSize);
          const searchParams = new URLSearchParams();
          searchParams.append('advancedSearch', JSON.stringify([{ field: 'requestNumber', operator: 'in', value: batch.join(',') }]));
          searchParams.append('page', '1');
          searchParams.append('pageSize', String(batchSize));
          const res = await api.get<PaginatedResponse>(`/api/requests?${searchParams}`);
          if (res?.items) {
            for (const item of res.items) {
              existingMap.set(item.requestNumber, item.id);
            }
          }
        }
      }

      const parseDateJP = (dateStr: string): string | null => {
        if (!dateStr) return null;
        const d = new Date(dateStr.replace(/\//g, '-'));
        return isNaN(d.getTime()) ? null : d.toISOString().split('T')[0];
      };

      for (let i = 0; i < importPreview.length; i++) {
        const row = importPreview[i];
        const rowNum = i + 2;

        try {
          const company = companies.find(c => c.name === row.企業名);
          if (!company && row.企業名) {
            errors.push(`行${rowNum}: 企業「${row.企業名}」が見つかりません`);
            continue;
          }

          const area = row.エリア名 ? areas.find(a => a.name === row.エリア名) : null;
          const assignee = row.担当者名 ? users.find(u => u.name === row.担当者名) : null;
          const status = statusCodes[row.ステータス] || row.ステータス || 'pending';
          const priority = priorityCodes[row.優先度] || row.優先度 || 'normal';

          const rawData: Record<string, unknown> = {
            title: row.タイトル || undefined,
            content: row.内容 || undefined,
            companyId: company?.id || undefined,
            areaId: area?.id || null,
            status: status || undefined,
            priority: priority || undefined,
            assignedTo: assignee?.id || null,
            dueDate: parseDateJP(row.期限日),
            emailSubject: row.メール件名 || null,
            emailFrom: row.メール送信者 || null,
            emailBody: row.メール本文 || null,
            emailDate: row.メール日時 ? new Date(row.メール日時.replace(/\//g, '-')).toISOString() : null,
          };

          const data = Object.fromEntries(
            Object.entries(rawData).filter(([, v]) => v !== undefined)
          );

          if (row.要請番号) {
            const existingId = existingMap.get(row.要請番号);
            if (existingId) {
              await api.put(`/api/requests/${existingId}`, data);
            } else {
              errors.push(`行${rowNum}: 要請番号「${row.要請番号}」が見つかりません`);
              continue;
            }
          } else {
            if (!rawData.title || !rawData.companyId) {
              errors.push(`行${rowNum}: タイトルと企業名は必須です`);
              continue;
            }
            await api.post('/api/requests', { ...data, content: data.content || '' });
          }
          successCount++;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : '更新に失敗しました';
          const errorDetails = (err as { details?: string })?.details ? ` (${(err as { details?: string }).details})` : '';
          errors.push(`行${rowNum}: ${errorMessage}${errorDetails}`);
          console.error(`Import error at row ${rowNum}:`, err);
        }
      }

      setImportErrors(errors);
      if (successCount > 0) {
        setToast({message: `${successCount}件のデータをインポートしました${errors.length > 0 ? `（${errors.length}件のエラー）` : ''}`, type: 'success'});
        fetchRequests();
      }
      if (errors.length === 0) {
        setShowImportModal(false);
        setImportPreview([]);
      }
    } catch (error) {
      console.error('Import failed:', error);
      setToast({message: 'インポートに失敗しました', type: 'error'});
    } finally {
      setImporting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setIsDirty(true);
  };

  const handleFileUpload = async (files: File[]): Promise<string[]> => {
    const urls = await api.uploadFiles(files, { entityType: 'request' });
    // アップロードされたファイル情報を保存
    const newFiles = files.map((file, index) => ({
      name: file.name,
      url: urls[index],
      type: file.type,
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
    return urls;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.companyId || !formData.content.trim()) {
      setToast({message: 'タイトル、企業、内容は必須です', type: 'error'});
      return;
    }
    if (submitting) return;

    setSubmitting(true);
    try {
      // 空文字のフィールドはnullに変換
      // PDFがある場合はpdfUrlに最初のPDFを設定
      const pdfFile = uploadedFiles.find(f => f.type === 'application/pdf');
      const submitData = {
        ...formData,
        areaId: formData.areaId || null,
        assignedTo: formData.assignedTo || null,
        dueDate: formData.dueDate || null,
        emailSubject: formData.emailSubject || null,
        emailBody: formData.emailBody || null,
        emailFrom: formData.emailFrom || null,
        emailDate: formData.emailDate || null,
        pdfUrl: pdfFile?.url || null,
        attachments: uploadedFiles.length > 0 ? uploadedFiles : null,
      };
      await api.post('/api/requests', submitData);
      setShowCreateModal(false);
      setFormData(initialFormData);
      setUploadedFiles([]);
      setIsDirty(false);
      fetchRequests();
    } catch (error) {
      console.error('Failed to create request:', error);
      setToast({message: '要請の登録に失敗しました', type: 'error'});
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (request: Request, newStatus: string) => {
    if (loading) return;
    try {
      await api.put(`/api/requests/${request.id}`, { status: newStatus });
      fetchRequests();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, 'default' | 'warning' | 'danger'> = {
      low: 'default',
      normal: 'default',
      high: 'warning',
      urgent: 'danger',
    };
    const labels: Record<string, string> = {
      low: '低',
      normal: '通常',
      high: '高',
      urgent: '緊急',
    };
    return <Badge variant={variants[priority] || 'default'}>{labels[priority] || priority}</Badge>;
  };

  const columns = [
    { key: 'requestNumber', header: '要請番号', minWidth: '120px', sortable: true, mobileTitle: true, render: (req: Request) => (
      <Link to={`/requests/${req.id}`} className="text-blue-600 hover:underline font-medium">
        {req.requestNumber}
        <span className="block text-xs text-gray-500 md:hidden truncate">{req.title}</span>
      </Link>
    )},
    { key: 'title', header: 'タイトル', minWidth: '200px', sortable: true, hideOnMobile: true, render: (req: Request) => (
      <Link to={`/requests/${req.id}`} className="text-gray-900 dark:text-white hover:text-blue-600 line-clamp-1">
        {req.title}
      </Link>
    )},
    { key: 'companyName', header: '企業名', minWidth: '150px', sortable: true },
    { key: 'status', header: 'ステータス', minWidth: '100px', sortable: true, render: (req: Request) => (
      <select
        value={req.status}
        onChange={(e) => {
          e.stopPropagation();
          handleStatusChange(req, e.target.value);
        }}
        className="text-xs bg-transparent border-none cursor-pointer w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <option value="pending">対応待ち</option>
        <option value="in_progress">対応中</option>
        <option value="completed">完了</option>
        <option value="cancelled">キャンセル</option>
      </select>
    )},
    { key: 'priority', header: '優先度', minWidth: '70px', sortable: true, render: (req: Request) => getPriorityBadge(req.priority) },
    { key: 'createdAt', header: '登録日', minWidth: '100px', sortable: true, hideOnMobile: true, render: (req: Request) => new Date(req.createdAt).toLocaleDateString('ja-JP') },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">要請応対</h1>
        <div className="flex gap-2 flex-wrap">
          {/* インポートボタン: 管理者とプロモーターのみ表示 */}
          {canImport && (
            <Button variant="secondary" onClick={handleImportCSV} className="w-full sm:w-auto">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              インポート
            </Button>
          )}
          <Button variant="secondary" onClick={handleExportCSV} disabled={exporting} className="w-full sm:w-auto">
            {exporting ? (
              <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
            {exporting ? 'エクスポート中...' : 'エクスポート'}
          </Button>
          {canCreateRequest && (
            <Button onClick={() => { setFormData(initialFormData); setUploadedFiles([]); setIsDirty(false); setShowCreateModal(true); }} className="w-full sm:w-auto">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              新規要請
            </Button>
          )}
        </div>
      </div>

      <Card>
        {/* 詳細検索 */}
        <div className="mb-4">
          <AdvancedSearch
            fields={searchFields}
            onSearch={handleSearch}
            quickSearchPlaceholder="要請番号・タイトルで検索..."
            quickSearchField="title"
            initialConditions={searchConditions}
          />
        </div>
        
        <Table
          columns={columns}
          data={requests}
          loading={loading}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        
        {/* ページネーション */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
            <span className="hidden sm:inline">表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => {
                setSearchParams(prev => {
                  prev.set('pageSize', e.target.value);
                  prev.delete('page');
                  return prev;
                });
              }}
              className="w-20 sm:w-24 text-sm"
            >
              <option value="10">10件</option>
              <option value="30">30件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
            </Select>
          </div>
          <div className="order-1 sm:order-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(newPage) => {
                setSearchParams(prev => {
                  prev.set('page', String(newPage));
                  return prev;
                });
              }}
            />
          </div>
        </div>
      </Card>

      <Modal isOpen={showCreateModal} onClose={() => { if (isDirty && !window.confirm('未保存の変更があります。閉じますか？')) return; setShowCreateModal(false); setFormData(initialFormData); setUploadedFiles([]); setIsDirty(false); }} title="新規要請" size="xl">
        <form onSubmit={handleCreate} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* 基本情報 */}
          <div className="border-b dark:border-gray-700 pb-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">基本情報</h3>
            <Input label="タイトル" name="title" value={formData.title} onChange={handleChange} required maxLength={500} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">企業 <span className="text-red-500">*</span></label>
                <Select name="companyId" value={formData.companyId} onChange={handleChange} required>
                  <option value="">企業を選択...</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">エリア</label>
                <Select name="areaId" value={formData.areaId} onChange={handleChange}>
                  <option value="">エリアを選択...</option>
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>{area.name}</option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          {/* ステータス・優先度・担当 */}
          <div className="border-b dark:border-gray-700 pb-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">管理情報</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ステータス</label>
                <Select name="status" value={formData.status} onChange={handleChange}>
                  <option value="pending">対応待ち</option>
                  <option value="in_progress">対応中</option>
                  <option value="completed">完了</option>
                  <option value="cancelled">キャンセル</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優先度</label>
                <Select name="priority" value={formData.priority} onChange={handleChange}>
                  <option value="low">低</option>
                  <option value="normal">通常</option>
                  <option value="high">高</option>
                  <option value="urgent">緊急</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">担当者</label>
                <Select name="assignedTo" value={formData.assignedTo} onChange={handleChange}>
                  <option value="">担当者を選択...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Input label="期限日" name="dueDate" type="date" value={formData.dueDate} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* 内容 */}
          <div className="border-b dark:border-gray-700 pb-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">内容</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">内容 <span className="text-red-500">*</span></label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows={4}
                required
                maxLength={10000}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder="要請の詳細内容を入力してください..."
              />
            </div>
          </div>

          {/* 資料アップロード */}
          <div className="border-b dark:border-gray-700 pb-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">資料（PDF・画像）</h3>
            <FileUpload
              onUpload={handleFileUpload}
              accept=".pdf,image/*"
              multiple={true}
              maxSize={20 * 1024 * 1024}
              maxFiles={10}
            />
            <p className="text-xs text-gray-500 mt-2">PDF・画像ファイルをアップロードできます（最大20MB/ファイル、最大10ファイル）</p>
          </div>

          {/* メール情報（オプション） */}
          <div className="pb-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">メール情報（オプション）</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="メール件名" name="emailSubject" value={formData.emailSubject} onChange={handleChange} maxLength={500} />
              <Input label="メール送信者" name="emailFrom" value={formData.emailFrom} onChange={handleChange} maxLength={255} />
              <div className="sm:col-span-2">
                <Input label="メール受信日時" name="emailDate" type="datetime-local" value={formData.emailDate} onChange={handleChange} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">メール本文</label>
                <textarea
                  name="emailBody"
                  value={formData.emailBody}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="メール本文を貼り付けてください..."
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={() => { if (isDirty && !window.confirm('未保存の変更があります。閉じますか？')) return; setShowCreateModal(false); setFormData(initialFormData); setUploadedFiles([]); setIsDirty(false); }} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? '登録中...' : '登録'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* インポートモーダル */}
      <Modal isOpen={showImportModal} onClose={() => { setShowImportModal(false); setImportPreview([]); setImportErrors([]); }} title="CSVインポート" size="xl">
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">インポートについて</h4>
            <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
              <li>• エクスポートしたCSVと同じフォーマットでインポートできます</li>
              <li>• <strong>要請番号</strong>がある行は既存データを更新します</li>
              <li>• 要請番号が空の行は新規データとして登録します</li>
              <li>• <strong>タイトル</strong>と<strong>企業名</strong>は必須です</li>
              <li>• 企業名・グループ名・担当者名は登録済みのものと一致する必要があります</li>
            </ul>
          </div>

          {importErrors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">エラー</h4>
              <ul className="text-xs text-red-700 dark:text-red-400 space-y-1 max-h-32 overflow-y-auto">
                {importErrors.map((err, i) => (
                  <li key={i}>• {err}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="border dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b dark:border-gray-700">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                プレビュー（{importPreview.length}件）
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left whitespace-nowrap">要請番号</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">タイトル</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">企業名</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">ステータス</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">優先度</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">担当者名</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.slice(0, 20).map((row, i) => (
                    <tr key={i} className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {row.要請番号 ? (
                          <span className="text-blue-600">{row.要請番号}</span>
                        ) : (
                          <span className="text-green-600 text-xs">(新規)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 max-w-[200px] truncate">{row.タイトル}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.企業名}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.ステータス}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.優先度}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.担当者名}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {importPreview.length > 20 && (
                <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 border-t dark:border-gray-700">
                  他 {importPreview.length - 20}件...
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={() => { setShowImportModal(false); setImportPreview([]); setImportErrors([]); }} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button onClick={executeImport} disabled={importing || importPreview.length === 0} className="w-full sm:w-auto">
              {importing ? (
                <>
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  インポート中...
                </>
              ) : (
                `${importPreview.length}件をインポート`
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
